import { computed, inject, Injectable } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ApiService } from '../../../core/services/api.service';
import { buildApiUrl } from '../../../core/config/api.constants';
import { AuthService } from '../../auth/services/auth.service';
import { MyPlayerLink, PlayerConnection } from '../models/player-connection.model';

/**
 * User-scoped store for pending coach-invitations addressed to the logged-in user.
 *
 * Single source of truth for the nav badge (AuthButtonsComponent) and the invitations
 * segment on the self-profile page. Reads a cross-team list from the user-scoped backend
 * endpoint; accept/decline reuse the existing team-scoped routes keyed by each row's
 * team_id. Raw httpResource (not ApiService) is used for the list so a transient error
 * never triggers the global 404 → /error404 redirect.
 */
@Injectable({ providedIn: 'root' })
export class MyInvitationsStore {
  private readonly api = inject(ApiService);
  private readonly authService = inject(AuthService);

  private readonly invitationsResource = httpResource<PlayerConnection[]>(() =>
    this.authService.isAuthenticated() ? buildApiUrl('/api/player-connections/my') : undefined,
  );

  private readonly linksResource = httpResource<MyPlayerLink[]>(() =>
    this.authService.isAuthenticated() ? buildApiUrl('/api/player-connections/my/links') : undefined,
  );

  readonly invitations = computed<PlayerConnection[]>(() => this.invitationsResource.value() ?? []);
  readonly pendingCount = computed<number>(() => this.invitations().length);
  readonly loading = computed(() => this.invitationsResource.isLoading());
  readonly error = computed(() => (this.invitationsResource.error() ? 'Failed to load invitations' : null));

  readonly links = computed<MyPlayerLink[]>(() => this.linksResource.value() ?? []);
  readonly linksLoading = computed(() => this.linksResource.isLoading());

  reload(): void {
    this.invitationsResource.reload();
    this.linksResource.reload();
  }

  private teamBase(teamId: number): string {
    return buildApiUrl(`/api/teams/${teamId}/player-connections`);
  }

  accept(inv: PlayerConnection): Observable<PlayerConnection> {
    return this.api
      .customPost<PlayerConnection>(`${this.teamBase(inv.team_id)}/${inv.id}/accept`, {})
      .pipe(tap(() => this.reload()));
  }

  decline(inv: PlayerConnection): Observable<PlayerConnection> {
    return this.api
      .customPost<PlayerConnection>(`${this.teamBase(inv.team_id)}/${inv.id}/decline`, {})
      .pipe(tap(() => this.reload()));
  }

  leave(link: MyPlayerLink): Observable<unknown> {
    return this.api
      .customPost<unknown>(`${this.teamBase(link.team_id)}/leave`, {})
      .pipe(tap(() => this.reload()));
  }
}
